// src/Gallery.tsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import UserAvatar from "../components/UserAvatar.tsx";
import LanguageSwitcher from "../components/LanguageSwitcher.tsx";
import MissingImage from "../components/MissingImage.tsx";
import PublishToggle from "../components/PublishToggle.tsx";
import ShareButton from "../components/ShareButton.tsx";
import { buildShareUrl } from "../lib/api.ts";
import { Toaster, toast } from "../components/toaster.tsx";

import CTAButton from "../components/CTAButton.tsx";
import { dict, getInitialLang, type Lang } from "../lib/i18n.ts";
import {
  getTG,
  initTG,
  getAvatarUrl,
  getUserName,
  vibrateTick,
  vibrateSuccess,
} from "../lib/telegram.ts";
import {
  fetchGallery,
  togglePublic,
  type GalleryItem,
} from "../lib/api.ts";
import NavBar from "../components/NavBar.tsx";
import MobileNav from "../components/MobileNav.tsx";

function SmartImage({
  src,
  alt,
  className,
}: {
  src?: string | null;
  alt: string;
  className?: string;
}) {
  const [broken, setBroken] = useState<boolean>(() => !src);

  useEffect(() => {
    setBroken(!src);
  }, [src]);

  if (broken) {
    return <MissingImage title={alt} />;
  }
  return (
    <img
      src={src as string}
      alt={alt}
      loading="lazy"
      className={className}
      onError={() => setBroken(true)}
    />
  );
}

function useInfiniteScroll(cb: () => void, enabled: boolean) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || !enabled) return;
    const io = new IntersectionObserver(
      (entries) => entries[0]?.isIntersecting && cb(),
      { rootMargin: "800px 0px", threshold: 0 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [cb, enabled]);
  return ref;
}

export default function Gallery() {
  // Telegram / язык
  const [lang, setLang] = useState<Lang>(() =>
    getInitialLang(getTG()?.initDataUnsafe?.user?.language_code ?? null)
  );
  const t = (k: keyof typeof dict["ru"]) => dict[lang][k];
  const tg = useMemo(() => initTG(), []);
  const tgId = tg?.initDataUnsafe?.user?.id ?? null;
  const name = getUserName();
  const avatar = getAvatarUrl();

  // состояние галереи
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(24);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<GalleryItem | null>(null);

  const loadingRef = useRef(false);
  const bootRef = useRef(true);

  const load = useCallback(async () => {
    if (!tgId) return;
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await fetchGallery(tgId, page, limit);
      setItems((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const fresh = res.items.filter((x) => !seen.has(x.id));
        return prev.concat(fresh);
      });
      const more = page * res.limit < res.total;
      setHasMore(more);
      if (more) setPage((p) => p + 1);
    } finally {
      loadingRef.current = false;
      setLoading(false);
      if (bootRef.current) bootRef.current = false;
    }
  }, [tgId, page, limit]);

  useEffect(() => {
    if (tgId) load();
  }, [tgId, load]);

  const loaderRef = useInfiniteScroll(() => {
    if (!bootRef.current && hasMore) load();
  }, !bootRef.current && hasMore);


  const toggleLang = () => {
    setLang((l) => {
      const n = l === "ru" ? "uz" : "ru";
      localStorage.setItem("lang", n);
      return n;
    });
  };

  // --- JSX ---
  return (
    <div className="min-h-screen-svh bg-grid relative text-white">
      <Toaster />
      <div className="absolute inset-0 grid-lines pointer-events-none"></div>


      {/* HEADER */}
      <div
          className="
      absolute left-1/2 -translate-x-1/2
      z-20
      top-[calc(env(safe-area-inset-top))]
    "
      >
        <div
            className="
          inline-flex items-center gap-2 text-[10px] md:text-xs
          font-semibold px-3 py-1 rounded-full glass
        "
        >
          <span className="size-2 rounded-full bg-emerald-400"></span>
         {t("my_gallery")}
        </div>
      </div>

      <header
          className="
        tg-safe-header
        relative z-10 max-w-6xl mx-auto
        h-12 md:h-16 flex items-center justify-between
        px-3 md:px-4
      "
      >
        <div className="flex items-center gap-2">
          <UserAvatar name={name} src={avatar ?? undefined}/>
          <span className="text-xs md:text-sm text-white/70">
      {dict[lang].welcome} {name}
    </span>
        </div>

        <div className="hidden md:flex">
          <NavBar/>
        </div>


        <div className="flex items-center gap-2">
          <LanguageSwitcher value={lang} onToggle={toggleLang}/>
        </div>
      </header>

      <MobileNav>
        <NavBar compact />
      </MobileNav>

      <main className="relative z-10 max-w-6xl mx-auto px-3 md:px-4 pt-8 md:pt-8">
        <div className="flex items-center justify-between mb-4">
          {!tgId && (
            <CTAButton as="a" href="https://t.me/gpt_5UzBot?startapp=1">
              {t("open_in_tg")}
            </CTAButton>
          )}
        </div>

        {!tgId ? (
          <div className="glass rounded-2xl p-6 text-center text-white/70">
            {t("my_empty")}
          </div>
        ) : (
            <>
              <div className="columns-2 md:columns-3 gap-3 [column-fill:_balance]">
                {items.map((it) => (
                    <article
                        key={it.id}
                        className={`mb-3 break-inside-avoid rounded-xl overflow-hidden bg-white/5 border ${
                            it.is_public
                                ? "border-emerald-400/30 shadow-[0_0_15px_rgba(52,211,153,0.15)]"
                                : "border-white/10"
                        } hover:border-white/20 transition`}
                    >
                      <button
                          className="block w-full"
                          onClick={() => setPreview(it)}
                          aria-label="Открыть превью"
                      >
                        <SmartImage
                            src={it.image_url}
                            alt={it.prompt}
                            className="w-full h-auto object-cover rounded-b-none transition-opacity duration-300"
                        />
                      </button>

                      <div className="p-3 space-y-2">
                        <p className="text-sm text-white/80 line-clamp-2">
                          {it.prompt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-white/60">
                          <div className="flex items-center gap-3">
                            <svg
                                width="18"
                                height="22"
                                viewBox="0 0 24 24"
                                className="drop-shadow-[0_0_10px_rgba(244,114,182,0.35)]"
                            >
                              <path
                                  d="M12.1 8.64L12 8.77l-.1-.13C10.14 6.6 7.11 6.28 5.5 8.09c-1.69 1.9-1.02 4.98 1.54 7.36C8.3 16.61 10.02 17.8 12 19c1.98-1.2 3.7-2.39 4.96-3.55 2.56-2.38 3.23-5.46 1.54-7.36-1.61-1.81-4.64-1.49-6.4.55Z"
                                  fill="currentColor"
                                  stroke="currentColor"
                                  strokeWidth="1.3"
                                  className="text-pink-500"
                              />
                            </svg>
                            {" "}
                            {it.likes}
                          </div>

                          <div className="grid grid-cols-2 gap-1">
                            <PublishToggle
                                initialPublic={it.is_public}
                                compact
                                onToggle={async (next) => {
                                  const {ok, is_public} = await togglePublic(
                                      it.id,
                                      next
                                  );
                                  if (!ok) throw new Error("toggle failed");
                                  setItems((arr) =>
                                      arr.map((x) =>
                                          x.id === it.id
                                              ? {...x, is_public}
                                              : x
                                      )
                                  );
                                }}
                                vibrateTick={vibrateTick}
                                vibrateSuccess={vibrateSuccess}
                                className="text-white/80 hover:text-white"
                            />

                            <ShareButton
                              title={t("my_gallery")}
                              text={it.prompt}
                              url={buildShareUrl(it.id)}
                              previewSrc={it.image_url ?? undefined}   // <-- имя строго previewSrc
                              compact
                              bottomOffset={88}
                              vibrateTick={vibrateTick}
                              vibrateSuccess={vibrateSuccess}
                              className="text-white/80 hover:text-white"
                            />

                          </div>
                        </div>
                      </div>
                    </article>
                ))}
              </div>

              <div ref={loaderRef} className="h-12 flex items-center justify-center">
                {loading && <div className="text-white/60 text-sm animate-pulse">{t("loading")}</div>}
                {!loading && !hasMore && items.length === 0 && (
                    <div className="text-white/60 text-sm">{t("empty")}</div>
                )}
                {!loading && !hasMore && items.length > 0 && (
                    <div className="text-white/40 text-xs">{t("no_more")}</div>
                )}
              </div>
            </>
        )}
      </main>

      {preview && (
          <div
              className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setPreview(null)}
          >
            <div
                className="max-w-3xl w-full"
                onClick={(e) => e.stopPropagation()}
            >
              <SmartImage
                  src={preview.image_url}
                  alt={preview.prompt}
                  className="w-full h-auto rounded-2xl"
            />
            <div className="mt-3 flex items-center justify-between gap-2">
              <p className="text-sm text-white/80 line-clamp-2">
                {preview.prompt}
              </p>
              <div className="flex items-center gap-2">
                <CTAButton
                   onClick={async () => { /* copy... */ toast(t("copy_prompt"), "success"); }}
                >
                   {t("copy_prompt")}
                </CTAButton>
                <CTAButton onClick={() => setPreview(null)}>{t("close")}</CTAButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
